import { useState } from "react";
import type { Provider } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import auroraLogo from "@/assets/aurora-brand-logo.svg";
import { PreviewShimCallback, previewShimSignInUrl } from "@/components/PreviewAuthShim";
import { previewAuthShimConfigFor, readShimCallback } from "@/lib/preview-auth-shim";
import { toast } from "sonner";

// Captured at module load — Supabase strips the URL hash on first auth
// init, so we have to look BEFORE that happens.
const arrivedWithAuthTokens =
  typeof window !== "undefined" &&
  /access_token=|refresh_token=|code=/.test(window.location.hash + window.location.search);
// Ephemeral preview builds sign in through dev and then mint a session on their
// own project, because Okta won't accept a per-PR callback address. Null on
// every other host, including dev/staging/prod. See
// src/lib/preview-auth-shim.ts.
const shimConfig = previewAuthShimConfigFor(
  typeof window !== "undefined" ? window.location.hostname : "",
);
// Also captured at module load, for the same reason as `arrivedWithAuthTokens`.
const shimCallback =
  shimConfig && typeof window !== "undefined"
    ? readShimCallback(window.location)
    : null;
// True when this document is rendered inside an iframe (e.g. Lovable
// preview pane). Accessing window.top can throw on cross-origin frames
// — treat that as "definitely embedded".
const isEmbedded = (() => {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();
// SSO breakout landing tab: tokens in URL AND we were opened by another
// window. In standalone (non-iframe) flows we just navigate the current
// tab to Okta and back, so this branch never triggers there.
const hasOpener =
  typeof window !== "undefined" && !!window.opener && window.opener !== window;
const hasBreakoutMarker =
  typeof window !== "undefined" && /[?&]sso_breakout=1\b/.test(window.location.search);
// Only show the "close this tab" landing when the sign-in was started
// from an embedded context (marker present) AND tokens are in the URL
// AND we still have an opener to message back to.
const isSsoBreakoutLanding = arrivedWithAuthTokens && hasOpener && hasBreakoutMarker;

// The only SSO provider configured for this workspace is Okta keyed to
// the @aurora.tech email domain — there is no need to ask the user for
// their email up front.
const SSO_DOMAIN = "aurora.tech";
// Okta via a Supabase custom OIDC provider (e.g. "custom:atms-okta-oidc").
// Set at build time by .github/workflows/deploy-frontend.yml for our own
// Supabase projects. Unset (Lovable's builds) keeps SAML SSO, which is what
// Lovable's Supabase project is configured for.
const OIDC_PROVIDER = import.meta.env.VITE_AUTH_OIDC_PROVIDER as string | undefined;

// Local development only. Okta SAML can't redirect back to localhost (the
// prod Supabase project's Redirect URL allowlist deliberately excludes it —
// see docs/plans/PREVIEW_SSO_REDIRECT_ALLOWLIST.md), so local dev signs in
// with email+password against the *dev* Supabase project configured in
// .env.local. `import.meta.env.DEV` is only true under `vite dev` — never in
// any `vite build` output, including `build:dev` — so this cannot ship.
function DevPasswordSignIn() {
  const [email, setEmail] = useState(import.meta.env.VITE_DEV_AUTH_EMAIL ?? "");
  const [password, setPassword] = useState(import.meta.env.VITE_DEV_AUTH_PASSWORD ?? "");
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        toast.error("Dev sign-in failed", { description: error.message });
        console.error("[Auth] dev signInWithPassword error", error);
        return;
      }
      // useAuth's onAuthStateChange picks the session up and the route
      // guard in App.tsx redirects away from /auth.
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSignIn} className="mt-6 space-y-3 border-t pt-4">
      <p className="text-xs text-muted-foreground">
        Local dev sign-in ({import.meta.env.VITE_SUPABASE_PROJECT_ID})
      </p>
      <div className="space-y-1">
        <Label htmlFor="dev-email" className="text-xs">Email</Label>
        <Input
          id="dev-email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="dev-password" className="text-xs">Password</Label>
        <Input
          id="dev-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <Button type="submit" variant="secondary" className="w-full" disabled={loading}>
        {loading ? "Signing in..." : "Dev sign-in"}
      </Button>
    </form>
  );
}

export default function Auth() {
  const [ssoLoading, setSsoLoading] = useState(false);
  // The eager postMessage/close that used to live here fired BEFORE
  // supabase had a chance to exchange the SSO tokens for a session,
  // which made the opener reload to /auth with no session. The
  // post-session signalling is now handled exclusively in useAuth's
  // onAuthStateChange (SIGNED_IN / INITIAL_SESSION).

  if (isSsoBreakoutLanding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md animate-fade-in">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img src={auroraLogo} alt="Aurora logo" className="h-12 w-12 object-contain" />
            </div>
            <CardTitle className="text-2xl">Sign-in complete</CardTitle>
            <CardDescription>
              You can safely close this tab and return to ATMS.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              className="w-full"
              onClick={async () => {
                try {
                  const { data } = await supabase.auth.getSession();
                  window.opener?.postMessage(
                    {
                      type: "AUTH_COMPLETE",
                      access_token: data.session?.access_token,
                      refresh_token: data.session?.refresh_token,
                    },
                    "*",
                  );
                } catch {
                  /* noop */
                }
                window.close();
              }}
            >
              Close this tab
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              If the tab doesn't close, switch back to your ATMS window — you're already signed in there.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (shimConfig && shimCallback) {
    return <PreviewShimCallback config={shimConfig} callback={shimCallback} />;
  }

  const handleSsoSignIn = async () => {
    const domain = SSO_DOMAIN;
    setSsoLoading(true);
    try {
      // Ephemeral preview: Okta won't accept this branch's own callback, so
      // sign in through dev and let PreviewShimCallback convert it into a
      // session on this project.
      if (shimConfig) {
        const url = await previewShimSignInUrl(
          shimConfig,
          `${window.location.origin}/auth`,
        );
        window.location.assign(url);
        return;
      }

      const options = {
        // When breaking out to a popup (embedded flow), tag the
        // landing URL so useAuth can recognize it as a popup tab
        // that should postMessage + self-close. Standalone same-tab
        // redirects must NOT carry this marker or the user's tab
        // would close on itself after sign-in.
        redirectTo: isEmbedded
          ? `${window.location.origin}/auth?sso_breakout=1`
          : `${window.location.origin}/`,
        // Prevent supabase-js from navigating the current window to
        // Okta — we want to open the IdP in a separate tab below.
        skipBrowserRedirect: true,
      };
      const { data, error } = OIDC_PROVIDER
        ? // supabase-js 2.93 types don't list custom:* providers yet; the
          // value is passed through to /auth/v1/authorize unchanged.
          await supabase.auth.signInWithOAuth({ provider: OIDC_PROVIDER as Provider, options })
        : await supabase.auth.signInWithSSO({ domain, options });
      if (error) {
        // Server-side rejection (e.g. unknown SSO domain, provider disabled).
        toast.error("SSO sign-in rejected", {
          description: `${error.message}${error.status ? ` (HTTP ${error.status})` : ""}`,
        });
        console.error("[Auth] SSO sign-in error", { method: OIDC_PROVIDER ?? `saml:${domain}`, error });
        return;
      }
      if (data?.url) {
        if (isEmbedded) {
          // Embedded in an iframe (Lovable preview). Navigating the
          // current frame to Okta would violate frame-ancestors CSP,
          // and the iframe's localStorage is partitioned away from a
          // top-level popup, so we break out to a new tab and rely on
          // postMessage(AUTH_COMPLETE, tokens) → setSession in useAuth.
          // Do NOT pass "noopener" in the features string — even
          // "noopener=no" is treated as the noopener token by Chrome
          // and nulls out window.opener in the new tab.
          const popup = window.open(data.url, "_blank");
          if (!popup) {
            toast.error("Popup blocked", {
              description:
                "Allow popups for this site to continue with SSO sign-in.",
            });
          }
        } else {
          // Standalone tab — just navigate to Okta. Same-tab redirect
          // shares localStorage with the post-SAML landing, so the
          // session lands directly without any cross-window plumbing.
          window.location.assign(data.url);
        }
      }
    } catch (err) {
      // Network-layer failure — fetch never reached the server (DNS, TLS,
      // CORS, extension, corporate proxy, captive portal). Surface the
      // underlying error name + message so users can report something
      // actionable instead of a generic "Failed to fetch".
      const e = err as Error;
      const name = e?.name ?? "Error";
      const message = e?.message ?? "Unknown network error";
      const isFailedToFetch = /failed to fetch|networkerror|load failed/i.test(message);
      toast.error(
        isFailedToFetch
          ? "Couldn't reach the sign-in service"
          : "Failed to start SSO sign-in",
        {
          description: isFailedToFetch
            ? `Network blocked the request (${name}: ${message}). Check VPN, ad-blockers, or your network and try again.`
            : `${name}: ${message}`,
          duration: 10_000,
        },
      );
      console.error("[Auth] SSO sign-in threw", { method: OIDC_PROVIDER ?? `saml:${domain}`, name, message, err });
    } finally {
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={auroraLogo} alt="Aurora logo" className="h-12 w-12 object-contain" />
          </div>
          <CardTitle className="text-2xl">ATMS</CardTitle>
          <CardDescription>Aurora Transportation Management System</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Button
              className="w-full"
              onClick={() => handleSsoSignIn()}
              disabled={ssoLoading}
            >
              {ssoLoading ? "Redirecting..." : "Continue with SSO"}
            </Button>
          </div>
          <div className="text-center mt-4">
            <a
              href="https://squad.cloud.aurora.tech/#/groups/access-atms?openJoinGroupDialog=true"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
            >
              Request access
            </a>
          </div>
          {import.meta.env.DEV ? <DevPasswordSignIn /> : null}
        </CardContent>
      </Card>
    </div>
  );
}
